<template>
  <div class="challenge-overview-page">
    <ChallengePageHero />

    <div class="challenge-overview-content">
      <div class="challenge-overview-title-bar">
        <div class="challenge-overview-heading">
          <p class="challenge-overview-kicker">Challenge Overview</p>
          <h2 class="challenge-overview-title">Introduction</h2>
        </div>
        <ActionButton
          variant="outline"
          size="large"
          custom-class="challenge-overview-open-btn"
          @click="scrollToChallenges"
        >
          Go to Challenges
        </ActionButton>
      </div>

      <section class="challenge-overview-intro" aria-labelledby="intro-heading">
        <div class="challenge-overview-intro__copy">
          <h2 id="intro-heading" class="challenge-overview-intro__title">
            Welcome to the SO-101 Robotics Lab!
          </h2>
          <p class="challenge-overview-intro__body">
            Power up your station and get ready to simulate, control, and code
            your 6-axis robotic arm. Work with your team to master motor
            movement, 3D space, distance sensors, and AI vision. Sets the
            hardware context and enables student teams to launch specific
            learning modules as directed by their teacher.
          </p>
        </div>
        <div class="challenge-overview-intro__media">
          <img
            src="/images/robotic-arm-challenges/intro-illustration.svg"
            alt="SO-101 robotic arm"
            width="742"
            height="680"
          />
        </div>
      </section>

      <section
        class="challenge-overview-hardware"
        aria-labelledby="hardware-heading"
      >
        <div class="challenge-overview-section-header">
          <div class="challenge-overview-section-icon">
            <img
              src="/images/robotic-arm-challenges/section-icon.svg"
              alt=""
              width="40"
              height="32"
            />
          </div>
          <div class="challenge-overview-hardware__titles">
            <h2 id="hardware-heading" class="challenge-overview-section-title">
              Hardware system map
            </h2>
            <p class="challenge-overview-hardware__subtitle">
              Interactive visual breakdown introducing the equipment used across
              the challenges
            </p>
          </div>
        </div>
        <img
          class="challenge-overview-hardware__map"
          src="/images/robotic-arm-challenges/hardware-system-map.png"
          alt="Hardware system map showing the robotic arm, webcam on tripod, USB gamepad controller, 3D-printed claw with servo, 3D-printed classification objects, and a laptop with camera that is not included."
          width="1376"
          height="918"
        />
      </section>

      <section
        id="unit-and-challenge"
        class="challenge-overview-units"
        aria-labelledby="units-heading"
      >
        <div class="challenge-overview-section-header">
          <div class="challenge-overview-section-icon">
            <img
              src="/images/robotic-arm-challenges/section-icon.svg"
              alt=""
              width="40"
              height="32"
            />
          </div>
          <h2 id="units-heading" class="challenge-overview-section-title">
            Challenges
          </h2>
        </div>
        <div class="challenge-overview-units__grid">
          <ChallengeUnitCard
            v-for="unit in units"
            :key="unit.unitLabel"
            :unit-label="unit.unitLabel"
            :title="unit.title"
            :description="unit.description"
            :category="unit.category"
            :status="unit.status"
            :to="unit.to"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChallengePageHero from "@/components/robotic-arm/ChallengePageHero.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import ChallengeUnitCard from "@/components/robotic-arm/ChallengeUnitCard.vue";
import { challengeStatus } from "@/components/robotic-arm/challengeProgress";

const challengeUnits = [
  {
    id: "2-1",
    unitLabel: "Challenge 2.1",
    title: "Servo Rotation Simulator",
    category: "Simulation",
    description:
      "Test physical rotation limits on a 2D claw schematic before driving real motors.",
    to: "/robotic-arm-challenges/2-1",
  },
  {
    id: "2-2",
    unitLabel: "Challenge 2.2",
    title: "Physical Claw Control",
    category: "Device",
    description: "Find the target angles on a real claw servo.",
    to: "/robotic-arm-challenges/2-2",
  },
  {
    id: "2-3",
    unitLabel: "Challenge 2.3",
    title: "Grab Practice",
    category: "Simulation",
    description: "Line the claw up and grab the object before it slips away.",
    to: "/robotic-arm-challenges/2-3",
  },
  {
    id: "3-1",
    unitLabel: "Challenge 3.1",
    title: "Whole-Arm Simulator",
    category: "Simulation",
    description: "Move each motor of a 3D arm to its target angle.",
    to: "/robotic-arm-challenges/3-1",
  },
  {
    id: "3-2",
    unitLabel: "Challenge 3.2",
    title: "Real Arm Digital Twin",
    category: "Device",
    description: "Match the real arm to a ghost pose on screen.",
    to: "/robotic-arm-challenges/3-2",
  },
  {
    id: "4-1",
    unitLabel: "Challenge 4.1",
    title: "Gesture AI",
    category: "AI",
    description: "Train an AI to recognise your hand gestures.",
    to: "/robotic-arm-challenges/4-1",
  },
  {
    id: "4-2",
    unitLabel: "Challenge 4.2",
    title: "Gesture-Controlled Real Arm",
    category: "Device",
    description:
      "Move a cube between baskets using hand gestures for the claw.",
    to: "/robotic-arm-challenges/4-2",
  },
  {
    id: "5",
    unitLabel: "Challenge 5",
    title: "Autonomous Movement, Part 1",
    category: "AI",
    description: "Teach an AI to repeat a task by showing it.",
    to: "/robotic-arm-challenges/5",
  },
];

const upcomingUnits = [];

const units = computed(() => [
  ...challengeUnits.map((unit) => ({
    ...unit,
    status: challengeStatus(unit.id),
  })),
  ...upcomingUnits.map((unit) => ({
    ...unit,
    status: "not-started",
  })),
]);

const scrollToChallenges = () => {
  document
    .getElementById("unit-and-challenge")
    ?.scrollIntoView({ behavior: "smooth" });
};
</script>

<style scoped>
.challenge-overview-page {
  font-family: var(--font-family-primary);
}

.challenge-overview-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px 32px 40px;
}

.challenge-overview-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.challenge-overview-heading {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.challenge-overview-kicker {
  margin: 0;
  color: var(--primary-blue);
  font-size: var(--font-size-headline-small);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-headline-small);
}

.challenge-overview-title {
  margin: 0;
  color: var(--primary-blue);
  font-size: var(--font-size-display-medium);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-medium);
}

.challenge-overview-content :deep(.challenge-overview-open-btn) {
  width: 256px;
  flex-shrink: 0;
  font-size: var(--font-size-title-big);
}

.challenge-overview-intro {
  display: grid;
  grid-template-columns: minmax(0, 658px) minmax(0, 654px);
  gap: 64px;
  align-items: center;
}

.challenge-overview-intro__copy {
  display: flex;
  flex-direction: column;
  gap: 64px;
}

.challenge-overview-intro__title {
  margin: 0;
  color: var(--blue-dark-2);
  font-size: var(--font-size-display-large);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-large);
  letter-spacing: -0.1425px;
}

.challenge-overview-intro__body {
  margin: 0;
  color: var(--neutral-700);
  font-size: var(--font-size-headline-medium);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-headline-medium);
}

.challenge-overview-intro__media img {
  display: block;
  width: 100%;
  height: auto;
}

.challenge-overview-hardware,
.challenge-overview-units {
  padding: 40px 0;
}

.challenge-overview-section-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.challenge-overview-hardware .challenge-overview-section-header {
  margin-bottom: 64px;
}

.challenge-overview-units .challenge-overview-section-header {
  margin-bottom: 24px;
}

.challenge-overview-section-icon {
  display: flex;
  align-items: center;
  height: 80px;
}

.challenge-overview-section-icon img {
  display: block;
  width: 40px;
  height: 32px;
}

.challenge-overview-section-title {
  margin: 0;
  color: var(--blue-dark-2);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
}

.challenge-overview-hardware__titles {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.challenge-overview-hardware__subtitle {
  margin: 0;
  max-width: 676px;
  color: var(--blue-dark-2);
  font-size: var(--font-size-headline-medium);
  font-weight: var(--font-weight-medium);
  line-height: normal;
  text-align: right;
}

.challenge-overview-hardware__map {
  display: block;
  width: 100%;
  height: auto;
}

.challenge-overview-units__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;
}

@media (max-width: 1200px) {
  .challenge-overview-units__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .challenge-overview-intro {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .challenge-overview-intro__copy {
    gap: 24px;
  }

  .challenge-overview-hardware__titles {
    flex-direction: column;
  }

  .challenge-overview-hardware__subtitle {
    max-width: none;
    text-align: left;
  }
}

@media (max-width: 768px) {
  .challenge-overview-content {
    padding: 24px 16px 40px;
  }

  .challenge-overview-title-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .challenge-overview-content :deep(.challenge-overview-open-btn) {
    width: 100%;
  }

  .challenge-overview-intro__title {
    font-size: var(--font-size-display-small);
    line-height: var(--line-height-display-small);
  }

  .challenge-overview-intro__body {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
  }

  .challenge-overview-section-icon {
    height: 48px;
  }

  .challenge-overview-units__grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
</style>
